import React, { useState, useEffect } from 'react';
import { Button, Form, Container, Alert, Card, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoMdPersonAdd } from 'react-icons/io';
import axios from 'axios';
import { useAuth } from '../AuthContext/AuthContext';
import { BASE_URL } from '../../Constants/constants';
import { sendEmail } from '../Utilities/Email';

// Global suppression of ResizeObserver error
const suppressResizeObserverError = () => {
    const originalError = console.error;
    console.error = (...args) => {
        const errorMessage = args[0];
        if (typeof errorMessage === 'string' && errorMessage.includes('ResizeObserver loop completed with undelivered notifications')) {
            return;
        }
        originalError(...args);
    };
};

// Debounce function to limit ResizeObserver calls
const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
};

const UserRegistrationPage = () => {
    const { t } = useTranslation('userManagement');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');     // ADDED: field-level email error
    const [usernameError, setUsernameError] = useState(''); // ADDED: field-level username error
    const { login } = useAuth();
    const [isRegistering, setIsRegistering] = useState(false);
    const navigate = useNavigate();

    const validateForm = () => {
        if (password !== passwordConfirm) {
            setError(t('messages.passwordMismatch'));
            return false;
        }
        if (password.length < 8 || !password.match(/^(?=.*[a-zA-Z])(?=.*[0-9])/)) {
            setError(t('messages.passwordRequirements'));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsRegistering(true);
        setError('');
        setEmailError('');     // ADDED: clear field errors on each attempt
        setUsernameError(''); // ADDED

        const abortController = new AbortController();

        const payload = {
            username: username.toLowerCase(),
            password,
            last_name: lastName,
            first_name: firstName,
            email: email.toLowerCase(),
            role_name: "STAFF",
            is_active: false,
        };

        try {
            const response = await axios.post(`${BASE_URL}/register/`, payload, {
                signal: abortController.signal,
            });
            if (response.status === 200 || response.status === 201) {
                const adminEmail = 'haiti.undp.management.system@undp.org';
                const msg = `Dear admin, A new user ${firstName} ${lastName} has registered. Please log into the platform to approve the user.`;
                sendEmail(email, adminEmail, msg);
                navigate('/registration-success');
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                console.warn('Request cancelled:', error.message);
            } else {
                console.error('Registration error:', error);
                const detail = error.response?.data?.detail;
                // ADDED: map backend error codes to field-level messages
                if (detail === 'EMAIL_ALREADY_EXISTS') {
                    setEmailError(t('messages.emailAlreadyExists'));
                } else if (detail === 'USERNAME_ALREADY_EXISTS') {
                    setUsernameError(t('messages.usernameAlreadyExists'));
                } else {
                    setError(detail || t('messages.error'));
                }
            }
        } finally {
            setIsRegistering(false);
        }
    };

    useEffect(() => {
        suppressResizeObserverError();

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            const handleResize = debounce(() => {}, 100);
            resizeObserver = new ResizeObserver(handleResize);
            const target = document.querySelector('.registration-container');
            if (target) resizeObserver.observe(target);
        }

        const abortController = new AbortController();

        return () => {
            abortController.abort();
            resizeObserver?.disconnect();
            setIsRegistering(false);
            setError('');
        };
    }, []);

    return (
        <div className="registration-container" style={{
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            paddingTop: '3rem',
            paddingBottom: '3rem'
        }}>
            <Container>
                <Row className="justify-content-center">
                    <Col xs={12} sm={10} md={8} lg={7} xl={6}>
                        <Card className="shadow-lg border-0">
                            <Card.Header className="bg-primary text-white text-center py-4">
                                <h2 className="mb-0">
                                    <IoMdPersonAdd className="me-2" size={28} />
                                    {t('registration.title')}
                                </h2>
                                <p className="mb-0 mt-2 text-white-50 fs-6">
                                    {t('registration.subtitle')}
                                </p>
                            </Card.Header>

                            <Card.Body className="p-4">
                                {error && (
                                    <Alert
                                        variant="danger"
                                        onClose={() => setError('')}
                                        dismissible
                                        className="mb-4"
                                    >
                                        {typeof error === 'object' ? JSON.stringify(error) : error}
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit} name="registration-form" id="registration-form">
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label htmlFor="regFirstName" className="fw-semibold">
                                                    {t('form.firstName')}
                                                </Form.Label>
                                                <Form.Control
                                                    id="regFirstName"
                                                    type="text"
                                                    placeholder={t('registration.enterFirstName')}
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    className="form-control-lg"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label htmlFor="regLastName" className="fw-semibold">
                                                    {t('form.lastName')}
                                                </Form.Label>
                                                <Form.Control
                                                    id="regLastName"
                                                    type="text"
                                                    placeholder={t('registration.enterLastName')}
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    className="form-control-lg"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3">
                                        <Form.Label htmlFor="regEmail" className="fw-semibold">
                                            {t('form.email')}
                                        </Form.Label>
                                        <Form.Control
                                            id="regEmail"
                                            type="email"
                                            placeholder={t('registration.enterEmail')}
                                            value={email}
                                            onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                                            className="form-control-lg"
                                            isInvalid={!!emailError}
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {emailError}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label htmlFor="regUsername" className="fw-semibold">
                                            {t('form.username')}
                                        </Form.Label>
                                        <Form.Control
                                            id="regUsername"
                                            aria-label={t('form.username')}
                                            type="text"
                                            placeholder={t('registration.chooseUsername')}
                                            value={username}
                                            onChange={(e) => { setUsername(e.target.value); setUsernameError(''); }}
                                            autoComplete="username"
                                            className="form-control-lg"
                                            isInvalid={!!usernameError}
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {usernameError}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label htmlFor="regPassword" className="fw-semibold">
                                            {t('form.password')}
                                        </Form.Label>
                                        <Form.Control
                                            id="regPassword"
                                            type="password"
                                            aria-label={t('form.password')}
                                            placeholder={t('registration.createPassword')}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            autoComplete="new-password"
                                            className="form-control-lg"
                                            required
                                        />
                                        <Form.Text className="text-muted">
                                            {t('registration.passwordHint')}
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label htmlFor="regPasswordConfirm" className="fw-semibold">
                                            {t('form.confirmPassword')}
                                        </Form.Label>
                                        <Form.Control
                                            id="regPasswordConfirm"
                                            type="password"
                                            placeholder={t('registration.confirmYourPassword')}
                                            value={passwordConfirm}
                                            onChange={(e) => setPasswordConfirm(e.target.value)}
                                            autoComplete="new-password"
                                            className="form-control-lg"
                                            required
                                        />
                                    </Form.Group>

                                    <div className="d-grid gap-2 mt-4 mb-3">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={isRegistering}
                                            size="lg"
                                            className="py-3 fw-semibold"
                                        >
                                            {isRegistering ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {t('registration.creating')}
                                                </>
                                            ) : (
                                                t('buttons.createAccount')
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>

                            <Card.Footer className="text-center py-4 bg-light border-top">
                                <p className="mb-0 text-muted">
                                    {t('registration.alreadyHaveAccount')}
                                    <a href="/login" className="text-primary text-decoration-none fw-semibold ms-1">
                                        {t('buttons.signIn')}
                                    </a>
                                </p>
                            </Card.Footer>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default UserRegistrationPage;